import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import CustomTable from '../../../shared/CustomTable'
import {
  exportCashInvoicesintoExcelColumns,
  InvoiceColumns
} from '../../../constants/BillingConstants'
import { EditIcon, EyeIcon, PrintIcon } from '../../../utils/TableActionIcons'
import CustomPagination from '../../../shared/CustomPagination'
import { useGetCashInvoicesQuery } from '../../../redux/api/modules/billingApi'
import ReturnUnpaidInvoiceForm from './ReturnUnpaidInvoiceForm'
import BillingFilters from './BillingFilters'
import { useTranslation } from 'react-i18next'
import { IoIosSend } from 'react-icons/io'
import { fetchData } from '../../../helpers/fetchData'
import toast from 'react-hot-toast'
import { FaHistory, FaRedo, FaEye } from 'react-icons/fa'
import { MdCancel, MdOutlineHistory } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { HiArrowUturnLeft } from 'react-icons/hi2'
import CancelInvoice from './CancelInvoice'
import RedoCancelInvoice from './RedoCancelInvoice'
import CheckPermission from '../../../hooks/CheckPermission'
import ZatcaHistoryModal from './ZatcaHistoryModal'
import ViewInvoiceModal from './ViewInvoiceModal'

import usePersistentBillingFilters from '../../../hooks/usePersistentBillingFilters'
import { hasBillingSearchFilters, shouldSkipInitialBillingLoad } from '../../../utils/businessLevelAccess'
const ReusableModal = React.lazy(() => import('../../../shared/ReuseableModal'))
const ConfirmationModal = React.lazy(() =>
  import('../../../shared/ConfirmationModal')
)

function CashInvoices () {
  const [payload, setPayload, resetPayload, appliedPayload, applyFilters] =
    usePersistentBillingFilters('cash')

  const [searchQuery, setSearchQuery] = useState(payload?.query || '')
  const navigate = useNavigate()
  const { t } = useTranslation()

  const auth = useSelector(state => state?.auth)
  const skipInitialBillingLoad = shouldSkipInitialBillingLoad(auth?.user)

  const queryPayload = React.useMemo(() => {
    const formData = {
      ...appliedPayload,
      filters: { ...appliedPayload?.filters }
    }
    formData.filters.cash_invoice_number = appliedPayload?.filters?.invoice_number
    delete formData?.filters?.invoice_number
    return formData
  }, [appliedPayload])

  const {
    data,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetCashInvoicesQuery(queryPayload, {
    skip: skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)
  })

  const loading = queryLoading || queryFetching
  const [cancelLoading, setCancelLoading] = useState(false)
  const isAdmin = useSelector(
    state => state.auth.user?.role?.name_en == 'Admin'
  )
  const isManager = useSelector(
    state => state.auth.user?.role?.name_en == 'manager'
  )
  const isAccountant = useSelector(
    state => state.auth.user?.role?.name_en == 'Accountant'
  )
  const isReceptionist = useSelector(
    state => state.auth.user?.role?.name_en == 'Receptionist'
  )
  const isEnabledEditAfterZatca = useSelector(
    state => state?.auth?.zatcaSettings?.enable_edit_zatca_posted_invoice
  )

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null)
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [openSendToZatcaModal, setOpenSendToZatcaModal] = useState(false)
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [zatcaLoading, setZatcaLoading] = useState(false)
  const [openZatcaHistoryModal, setOpenZatcaHistoryModal] = useState(false)
  const [zatcaHistoryData, setZatcaHistoryData] = useState(null)
  const [zatcaHistoryLoading, setZatcaHistoryLoading] = useState(null)
  const { hasPermission } = CheckPermission()
  const isHaveUpdateInvoicePermission = hasPermission('invoices', 'update')
  const [openRedoModal, setOpenRedoModal] = useState(false)
  const [openViewModal, setOpenViewModal] = useState(false)
  const [viewInvoiceNumber, setViewInvoiceNumber] = useState(null)
  const [viewSimpleInvNo, setViewSimpleInvNo] = useState(null)
  const [viewFileNumber, setViewFileNumber] = useState(null)
  const [viewPatientId, setViewPatientId] = useState(null)
  const [viewDocumentId, setViewDocumentId] = useState(null)

  const actionsCoumn = {
    body: actionsTemplate,
    header: t('Actions'),
    headerCellClass: 'flex items-center justify-center'
  }
  const columnsData = [actionsCoumn, ...InvoiceColumns(t, auth)]

  const fetchInitialData = () => {
    refetch()
  }

  const getZatcaHistory = async invoiceNumber => {
    setOpenZatcaHistoryModal(true)
    setZatcaHistoryLoading(true)
    setSelectedInvoice(invoiceNumber)
    try {
      const response = await fetchData(
        'GET',
        `/v3/zatca/history/${invoiceNumber}`
      )
      if (response?.success) {
        setZatcaHistoryData(response)
      } else {
        toast.error(response?.message || 'Failed to fetch ZATCA history')
        setOpenZatcaHistoryModal(false)
      }
    } catch (error) {
      toast.error('Failed to fetch ZATCA history')
      setOpenZatcaHistoryModal(false)
    } finally {
      setZatcaHistoryLoading(false)
    }
  }

  const getInvoiceAuditHistory = invoiceNumber => {
    navigate('/billing/update-history', {
      state: { invoice_number: invoiceNumber }
    })
  }


  function actionsTemplate (rowData) {
    const isCancelled = rowData?.is_patient_cancelled
    return (
      <div className='flex items-center justify-center gap-2'>
        <EyeIcon
          title={t('View Invoice')}
          size='18'
          onClick={() => {
            setViewInvoiceNumber(
              rowData?.cash_invoice_number || rowData?.invoice_number
            )
            setViewSimpleInvNo(rowData?.invoice_number)
            setViewFileNumber(rowData?.patient?.file_no || rowData?.file_no)
            setViewPatientId(
              rowData?.patient?.patient_id || rowData?.patient_id
            )
            setViewDocumentId(
              rowData?.patient?.document_id || rowData?.document_id
            )
            setOpenViewModal(true)
          }}
        />
        {isHaveUpdateInvoicePermission &&
          (isAdmin || isManager || isAccountant) &&
          !isCancelled &&
          rowData?.zatca_status != 'REPORTED' && (
            <>
              <MdCancel
                title={t('CancelInvoice')}
                className='text-red-500 cursor-pointer disabled:text-blue/20'
                size='18'
                onClick={() => {
                  setSelectedInvoiceData(rowData)
                  setOpenCancelModal(true)
                }}
              />
              <HiArrowUturnLeft
                title={t('ReturnInvoice')}
                className='text-red-500 cursor-pointer disabled:text-blue/20'
                size='18'
                onClick={() => {
                  setSelectedInvoiceData(rowData)
                  setOpenConfirmationModal(true)
                }}
              />
            </>
          )}
        {!isReceptionist &&
          !isCancelled &&
          isHaveUpdateInvoicePermission &&
          (rowData?.zatca_status !== 'REPORTED' ||
            (rowData?.zatca_status === 'REPORTED' &&
              isEnabledEditAfterZatca)) && (
            <EditIcon
              title={t('EditInvoice')}
              iconSize='18'
              onClick={() => {
                navigate(`/billing/edit-invoice/${rowData?.invoice_number}`, {
                  state: {
                    invoiceData: rowData,
                    patientId:
                      rowData?.patient?.patient_id || rowData?.patient_id,
                    documentId:
                      rowData?.patient?.document_id || rowData?.document_id,
                    fileNumber: rowData?.patient?.file_no || rowData?.file_no,
                    returnTo: '/billing',
                    previousState: {
                      page: payload.page,
                      per_page: payload.per_page,
                      filters: payload.filters
                    }
                  }
                })
              }}
            />
          )}
        {(isAdmin ||isManager|| isAccountant) &&
          isCancelled &&
          rowData?.zatca_status != 'REPORTED' && (
            <FaRedo
              title={t('Redo Cancel')}
              className='text-green-500 cursor-pointer disabled:text-blue/20'
              size='18'
              onClick={() => {
                setSelectedInvoiceData(rowData)
                setOpenRedoModal(true)
              }}
            />
          )}

        <PrintIcon
          title={t('PrintInvoice')}
          iconSize='20'
          onClick={() =>
            navigate(`/print-invoice/${rowData?.invoice_number}`, {
              state: {
                invoiceNumber: rowData?.invoice_number,
                state: {
                  file_no: rowData?.patient?.file_no
                },
                activeTabIndex: 0,
                activeChildTabIndex: 0
              }
            })
          }
        />
        {!isReceptionist &&
          !isCancelled &&
          rowData?.zatca_status == 'REPORTED' && (
            <FaHistory
              className='text-teal-500 cursor-pointer hover:text-teal-600'
              size={16}
              title={t('ZATCA History')}
              onClick={() =>
                getZatcaHistory(
                  rowData?.cash_invoice_number || rowData?.invoice_number
                )
              }
            />
          )}
        {!isReceptionist && rowData?.last_updated_by && (
          <MdOutlineHistory
            className='text-indigo-500 cursor-pointer hover:text-indigo-600'
            size={18}
            title={t('View Audit History')}
            onClick={() => getInvoiceAuditHistory(rowData?.invoice_number)}
          />
        )}
        {!isReceptionist &&
          rowData?.zatca_status != 'REPORTED' &&
          hasPermission('zatca', 'get') &&
          !isCancelled && (
            <IoIosSend
              className={`text-green-500  cursor-pointer `}
              title={t('Send to zatca')}
              size={20}
              onClick={async () => {
                setOpenSendToZatcaModal(true)
                setSelectedInvoice(rowData)
              }}
            />
          )}
      </div>
    )
  }

  const onPageChange = async (newPage, newRowsPerPage) => {
    setSelectedInvoices([])
    setPayload(prevPayload => ({
      ...prevPayload,
      cash_invoice_number: prevPayload?.invoice_number,
      page: newPage,
      per_page: newRowsPerPage
    }))
  }

  const handleConfirmReturn = async data => {
    if (!selectedInvoiceData) return
    setCancelLoading(true)
    try {
      const res = await fetchData('POST', '/v3/invoices/return', {
        invoice_number: selectedInvoiceData?.invoice_number,
        refund_reason: data?.reason,
        invoice_type: data?.invoice_type,
        patient_refund_amount: Number(data?.patient_refund_amount),
        returned_item: data?.invoice_details?.map(itm => ({
          ...itm,
          total_vat: Number(itm?.total_vat)
        }))
      })

      if (res) {
        toast.success(res?.message)
        setSelectedInvoiceData(null)
        setOpenConfirmationModal(false)
        fetchInitialData()
      }
    } catch (error) {
      toast.error('Failed to return invoice')
    } finally {
      setCancelLoading(false)
    }
  }
  const handleFetchZatcaStats = () => {
    navigate('/zatca-stats')
  }
  const handleSendToZatca = async () => {
    if (!selectedInvoice?.cash_invoice_number) {
      toast.error('No invoice number found.')
      return
    }
    setZatcaLoading(true)
    const response = await fetchData(
      'POST',
      '/v3/zatca/sendPatientInvoiceToZatca',
      {
        cash_invoice_number: selectedInvoice?.cash_invoice_number
      }
    )
    if (response?.message) {
      setOpenSendToZatcaModal(false)
      setSelectedInvoice(null)
      toast.success(response?.message)
      fetchInitialData()
    }
    setZatcaLoading(false)
  }

  return (
    <div className=''>
      <div className='flex items-center  mb-2 xl:mb-0'>
        <BillingFilters
          applyFilters={applyFilters}
          setSearchQuery={setSearchQuery}
          fetchInitialData={fetchInitialData}
          searchQuery={searchQuery}
          payloadData={payload}
          setPayloadData={setPayload}
          refetch={refetch}
          invoiceType='cash'
          resetFilters={() => {
            resetPayload()
            setSearchQuery('')
          }}
          name={'cash_invoices'}
          handleFetchZatcaStats={handleFetchZatcaStats}
          exportApiUrl={'/v3/invoices/exportPatientExcel'}
          exportOptionsFormat={exportCashInvoicesintoExcelColumns}
        />

      </div>
      <CustomTable
        isLoading={loading}
        data={data?.data || []}
        columns={columnsData}
        rowClassName={row => (row?.is_patient_cancelled ? '!bg-red-100' : '')}
      />
      {data?.data?.length > 0 && !loading && (
        <CustomPagination
          currentPage={data?.page || payload?.page || 1}
          rowsPerPage={data?.per_page || payload?.per_page || 20}
          totalRecords={data?.totalRecords}
          totalPages={data?.totalPages}
          onPageChange={onPageChange}
        />
      )}
      <ReusableModal
        isVisible={openCancelModal}
        title={t(`Cancel Invoice ${selectedInvoiceData?.cash_invoice_number}`)}
        HeaderIcon={MdCancel}
        position='top'
        onClose={() => {
          setSelectedInvoiceData(null)
          setOpenCancelModal(false)
          setCancelLoading(false)
        }}
      >
        <CancelInvoice
          refetch={fetchInitialData}
          setOpenCancelModal={setOpenCancelModal}
          setSelectedInvoiceData={setSelectedInvoiceData}
          selectedInvoiceData={selectedInvoiceData}
        />
      </ReusableModal>

      <ReusableModal
        isVisible={openConfirmationModal}
        title={t(`Refund Invoice ${selectedInvoiceData?.cash_invoice_number}`)}
        HeaderIcon={HiArrowUturnLeft}
        position='top'
        className='w-full md:w-[90%] '
        onClose={() => {
          setSelectedInvoiceData(null)
          setOpenConfirmationModal(false)
          setCancelLoading(false)
        }}
      >
        <ReturnUnpaidInvoiceForm
          handleCancel={handleConfirmReturn}
          cancelLoading={cancelLoading}
          selectedInvoiceData={selectedInvoiceData}
        />
      </ReusableModal>
      <ConfirmationModal
        visible={openSendToZatcaModal}
        setVisible={setOpenSendToZatcaModal}
        header={'Confirmation'}
        // message={`Are you sure you want to send invoice ${selectedInvoice?.cash_invoice_number} to Zatca?`}
        message={`${t('Are you sure you want to send invoice')} ${selectedInvoice?.cash_invoice_number} ${t('to Zatca?')}`}
        acceptLabel={'Send To Zatca'}
        rejectLabel={'Cancel'}
        onAccept={handleSendToZatca}
        isLoading={zatcaLoading}
      />

      <ReusableModal
        isVisible={openRedoModal}
        title={t(
          `Redo Cancel Invoice ${selectedInvoiceData?.cash_invoice_number}`
        )}
        HeaderIcon={FaRedo}
        position='top'
        onClose={() => {
          setSelectedInvoiceData(null)
          setOpenRedoModal(false)
        }}
      >
        <RedoCancelInvoice
          refetch={fetchInitialData}
          setOpenRedoModal={setOpenRedoModal}
          setSelectedInvoiceData={setSelectedInvoiceData}
          selectedInvoiceData={selectedInvoiceData}
        />
      </ReusableModal>
      <ReusableModal
        isVisible={openZatcaHistoryModal}
        title={t(`ZATCA Submission History Of     #${selectedInvoice}`)}
        HeaderIcon={FaHistory}
        position='top'
        onClose={() => {
          setOpenZatcaHistoryModal(false)
          setZatcaHistoryData(null)
          setSelectedInvoice(null)
        }}
        className='w-full md:w-[90%] lg:w-[80%]'
      >
        <ZatcaHistoryModal
          historyData={zatcaHistoryData}
          loading={zatcaHistoryLoading}
        />
      </ReusableModal>

      <ReusableModal
        isVisible={openViewModal}
        title={t('View Invoice Details')}
        HeaderIcon={FaEye}
        position='top'
        onClose={() => {
          setOpenViewModal(false)
          setViewInvoiceNumber(null)
          setViewFileNumber(null)
          setViewPatientId(null)
          setViewDocumentId(null)
        }}
        className='w-full md:w-[95%] lg:w-[95%] xl:w-[90%]'
      >
        <ViewInvoiceModal
          invoiceNumber={viewInvoiceNumber}
          fileNumber={viewFileNumber}
          patientId={viewPatientId}
          documentId={viewDocumentId}
          simpleInvNo={viewSimpleInvNo}
          isCash={true}
          onClose={() => {
            setOpenViewModal(false)
            setViewSimpleInvNo(null)
            setViewInvoiceNumber(null)
            setViewFileNumber(null)
            setViewPatientId(null)
            setViewDocumentId(null)
          }}
        />
      </ReusableModal>

    </div>
  )
}

export default CashInvoices